import { useState } from 'react';
import { FixedValue, FixedValueCreate, FixedValueUpdate } from '../types/costwand.d';
import { useFixedValues } from '../hooks/useFixedValues.ts';
import FixedValueForm from '../components/FixedValueForm.tsx';
import FixedValuesTable from '../components/FixedValuesTable.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  // DialogFooter, // Removed DialogFooter as it's not used for the form anymore
} from '../components/ui/dialog.tsx';
// import { useError } from '../context/ErrorContext.tsx'; // Not needed as apiClient handles global error

const FixedValuePage = () => {
  const {
    fixedValues,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createFixedValue,
    updateFixedValue,
    deleteFixedValue,
    fetchFixedValues,
  } = useFixedValues();

  // const { displayError } = useError(); // Not needed as apiClient handles global error

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingFixedValue, setEditingFixedValue] = useState<FixedValue | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  // Removed states for the separate error dialog for form submissions
  // const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  // const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);

  const handleAddFixedValue = () => {
    setEditingFixedValue(null);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleEditFixedValue = (fixedValue: FixedValue) => {
    setEditingFixedValue(fixedValue);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleDeleteFixedValue = async (id: number) => {
    await deleteFixedValue(id);
    fetchFixedValues(currentPage);
  };

  const handleFormSubmit = async (data: FixedValueCreate) => {
    setFormSubmitting(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);

    try {
      let success = false;
      if (editingFixedValue) {
        // Cast data to FixedValueUpdate as it's an update operation
        success = await updateFixedValue(editingFixedValue.id!, data as FixedValueUpdate);
      } else {
        success = await createFixedValue(data);
      }

      if (success) {
        setIsFormOpen(false);
        fetchFixedValues(currentPage);
      } else {
        // The global error handler in apiClient would have already displayed the error.
        // We just need to ensure the form stays open if it failed.
        // displayError(error || "An unexpected error occurred during form submission."); // If we wanted to explicitly trigger global error here
        // setIsErrorDialogOpen(true); // Removed local error dialog trigger
      }
    } catch (e: any) {
      // The global error handler in apiClient would have already displayed the error.
      // displayError(e.message || "An unexpected error occurred."); // If we wanted to explicitly trigger global error here
      // setIsErrorDialogOpen(true); // Removed local error dialog trigger
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingFixedValue(null);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  // Removed handleCloseErrorDialog function

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Fixed Value Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddFixedValue}>Add New Fixed Value</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingFixedValue ? 'Edit Fixed Value' : 'Add New Fixed Value'}</DialogTitle>
              <DialogDescription>
                {editingFixedValue ? 'Make changes to the fixed value here.' : 'Add a new fixed value to your list.'}
              </DialogDescription>
            </DialogHeader>
            <FixedValueForm
              initialData={editingFixedValue}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              // error={errorDialogMessage} // Removed the error prop
            />
          </DialogContent>
        </Dialog>
      </div>

      <FixedValuesTable
        fixedValues={fixedValues}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditFixedValue}
        onDelete={handleDeleteFixedValue}
        loading={loading}
        error={error}
        baseEndpoint="/fixed_values" // NEW: Pass the base endpoint
      />

      {/* Removed Error Dialog for form submission errors */}
      {/*
      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Error</DialogTitle>
            <DialogDescription>
              An error occurred during the operation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-red-600">
            {errorDialogMessage || 'An unexpected error occurred.'}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
};

export default FixedValuePage;